import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LeaderboardEntry } from "@/lib/api/leaderboard";
import { formatDistanceToNow } from "date-fns";
import { Trophy } from "lucide-react";

interface LeaderboardTableProps {
  entries: LeaderboardEntry[];
  currentUserId?: string;
  showCpd?: boolean;
}

export function LeaderboardTable({ entries, currentUserId, showCpd = true }: LeaderboardTableProps) {
  const getMedalEmoji = (rank: number) => {
    switch (rank) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return null;
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Trophy className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold mb-2">No leaderboard data yet</h3>
        <p className="text-muted-foreground mb-4">
          Complete a simulation to get started!
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Rank</TableHead>
            <TableHead>User</TableHead>
            <TableHead className="hidden md:table-cell">Agent</TableHead>
            <TableHead className="text-right">Best Score</TableHead>
            <TableHead className="text-right hidden sm:table-cell">Avg Score</TableHead>
            <TableHead className="text-right">Sessions</TableHead>
            <TableHead className="text-right hidden lg:table-cell">Coaching</TableHead>
            {showCpd ? <TableHead className="text-right hidden xl:table-cell">CPD Points</TableHead> : null}
            <TableHead className="text-right hidden lg:table-cell">Last Active</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => {
            const isCurrentUser = entry.user_id === currentUserId;
            const medal = getMedalEmoji(entry.rank);

            return (
              <TableRow
                key={`${entry.user_id}-${entry.agent_id}`}
                className={isCurrentUser ? "bg-primary/10 hover:bg-primary/15" : ""}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {medal ? (
                      <span className="text-2xl">{medal}</span>
                    ) : (
                      <span className="text-muted-foreground">#{entry.rank}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={entry.avatar_url} alt={entry.user_name || 'User'} />
                      <AvatarFallback className="text-xs">
                        {getInitials(entry.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="font-medium">{entry.user_name || 'Unknown User'}</span>
                      {entry.company_name && (
                        <span className="text-xs text-muted-foreground">
                          {entry.company_name}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <span className="text-sm">{entry.agent_name}</span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-lg font-bold text-primary">
                    {entry.best_score}
                  </span>
                </TableCell>
                <TableCell className="text-right hidden sm:table-cell">
                  <span className="text-sm text-muted-foreground">
                    {entry.average_score?.toFixed(1) || 'N/A'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-sm">{entry.total_sessions}</span>
                </TableCell>
                <TableCell className="text-right hidden lg:table-cell">
                  <span className="text-sm">{entry.coaching_sessions || 0}</span>
                </TableCell>
                {showCpd ? (
                  <TableCell className="text-right hidden xl:table-cell">
                    <span className="text-sm font-medium text-primary">
                      {entry.total_cpd_points || 0}
                    </span>
                  </TableCell>
                ) : null}
                <TableCell className="text-right hidden lg:table-cell">
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(entry.last_session_date), {
                      addSuffix: true,
                    })}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
